/**
 * CapturePage - 撮影画面コンポーネント
 */

import { useState, useRef, useCallback, useEffect } from 'react'
import { useStore } from '../store'
import { useCamera } from '../features/capture/hooks/useCamera'
import { useNavigationGuard } from '../hooks/useNavigationGuard'
import { useDeviceOrientation } from '../features/capture/hooks/useDeviceOrientation'
import { ModeSelector } from '../features/capture/components/ModeSelector'
import { OrientationGuide } from '../features/capture/components/OrientationGuide'
import { CameraPreview } from '../features/capture/components/CameraPreview'
import { CaptureControls } from '../features/capture/components/CaptureControls'
import { BrowserCameraAdapter } from '../core/services/camera/BrowserCameraAdapter'
import type { CaptureMode } from '../features/capture/types'
import { logger } from '../lib/logger'

export function CapturePage() {
  logger.log('[CapturePage] Component rendering')
  
  const navigateTo = useStore(state => state.navigateTo)
  const createSet = useStore(state => state.createSet)
  const addImageToSet = useStore(state => state.addImageToSet)
  const [captureSetId, setCaptureSetId] = useState<string | null>(null)
  
  // Use navigation guard for safe navigation
  const { safeNavigate, isNavigating } = useNavigationGuard()
  
  // Track mounted state to prevent state updates after unmount
  const isMountedRef = useRef(true)
  // Store timeouts for cleanup
  const timeoutRefs = useRef<Set<NodeJS.Timeout>>(new Set())
  
  // Browser back button handling
  useEffect(() => {
    // Push a new history entry when entering capture mode
    window.history.pushState({ page: 'capture' }, '', null)
    
    const handlePopState = (e: PopStateEvent) => {
      // When back button is pressed, navigate to home
      e.preventDefault()
      navigateTo('home')
    }
    
    window.addEventListener('popstate', handlePopState)
    
    return () => {
      window.removeEventListener('popstate', handlePopState)
    }
  }, [navigateTo])

  // ライフサイクルログ
  useEffect(() => {
    logger.log('[CapturePage] Component MOUNTED')
    isMountedRef.current = true
    
    const timeouts = timeoutRefs.current
    return () => {
      logger.log('[CapturePage] Component UNMOUNTING')
      isMountedRef.current = false
      
      // Clear all pending timeouts
      timeouts.forEach(timeout => clearTimeout(timeout))
      timeouts.clear()
    }
  }, [])
  
  // 状態管理
  const [mode, setMode] = useState<CaptureMode | null>(null)
  const [showOrientationGuide, setShowOrientationGuide] = useState(false)
  const [showGrid, setShowGrid] = useState(false)
  const [showGhost, setShowGhost] = useState(false)
  const [capturedImages, setCapturedImages] = useState<string[]>([])
  const [lastCapturedImage, setLastCapturedImage] = useState<string | undefined>()
  const [errorDismissed, setErrorDismissed] = useState(false)
  const [isCapturing, setIsCapturing] = useState(false)
  
  // カメラ関連
  const videoRef = useRef<HTMLVideoElement>(null)
  const cameraAdapter = useRef(new BrowserCameraAdapter())
  const cameraStarted = useRef(false)
  const orientation = useDeviceOrientation()
  
  const {
    isActive,
    isLoading,
    error,
    availableCameras,
    currentCamera,
    zoomLevel,
    torchEnabled,
    startCamera,
    stopCamera,
    captureImage,
    switchCamera,
    setZoom,
    setTorch,
    setCameraService
  } = useCamera()
  
  // 初期化
  useEffect(() => {
    setCameraService(cameraAdapter.current)
  }, [setCameraService])
  
  // エラーが変更されたらリセット
  useEffect(() => {
    if (error) {
      setErrorDismissed(false)
    }
  }, [error])
  
  // モード選択ハンドラ
  const handleSelectMode = useCallback((selectedMode: CaptureMode) => {
    setMode(selectedMode)
    
    // パノラマモードで縦向きの場合、回転ガイドを表示
    if (selectedMode === 'panorama' && orientation === 'portrait') {
      setShowOrientationGuide(true)
    } else {
      // カメラを起動（次のレンダリングサイクルで実行）
      const timeout = setTimeout(() => {
        if (!isMountedRef.current) return // Skip if unmounted
        
        logger.log('[CapturePage] Starting camera, videoRef.current:', videoRef.current, 'cameraStarted:', cameraStarted.current)
        if (videoRef.current && !cameraStarted.current) {
          cameraStarted.current = true
          void startCamera(videoRef.current)
        } else if (cameraStarted.current) {
          logger.log('[CapturePage] Camera already started, skipping')
        } else {
          logger.error('[CapturePage] videoRef.current is still null!')
        }
        
        timeoutRefs.current.delete(timeout)
      }, 100)
      timeoutRefs.current.add(timeout)
    }
  }, [orientation, startCamera])
  
  // 向き変更の継続ハンドラ
  const handleOrientationContinue = useCallback(() => {
    setShowOrientationGuide(false)
    const timeout = setTimeout(() => {
      if (!isMountedRef.current) return // Skip if unmounted
      
      if (videoRef.current && !cameraStarted.current) {
        cameraStarted.current = true
        void startCamera(videoRef.current)
      }
      
      timeoutRefs.current.delete(timeout)
    }, 100)
    timeoutRefs.current.add(timeout)
  }, [startCamera])
  
  // 向きガイドを無視
  const handleOrientationIgnore = useCallback(() => {
    setShowOrientationGuide(false)
    const timeout = setTimeout(() => {
      if (!isMountedRef.current) return // Skip if unmounted
      
      if (videoRef.current && !cameraStarted.current) {
        cameraStarted.current = true
        void startCamera(videoRef.current)
      }
      
      timeoutRefs.current.delete(timeout)
    }, 100)
    timeoutRefs.current.add(timeout)
  }, [startCamera])
  
  // 撮影処理中フラグ用のref（レンダリングを引き起こさない）
  const isCapturingRef = useRef(false)
  
  // 撮影ハンドラ
  const handleCapture = useCallback(async () => {
    // 二重クリック防止 - refを使用してレンダリング間で状態を保持
    if (isCapturingRef.current) {
      logger.log('[CapturePage] Capture already in progress, ignoring')
      return
    }
    
    logger.log('[CapturePage] handleCapture called')
    logger.log('[CapturePage] Camera state before capture - cameraStarted:', cameraStarted.current)
    
    try {
      isCapturingRef.current = true
      setIsCapturing(true)
      logger.log('[CapturePage] Capture started, calling captureImage()...')
      const frame = await captureImage()
      logger.log('[CapturePage] captureImage returned, frame:', !!frame)
      
      if (!frame) {
        logger.error('[CapturePage] Failed to capture frame')
        // エラーでもカメラを閉じない - 早期リターン
        return
      }
      
      // セットが未作成の場合は作成
      let setId = captureSetId
      if (!setId) {
        const newSet = createSet(
          mode === 'panorama' ? 'パノラマ撮影' : '通常撮影',
          mode === 'panorama' ? 'circular' : 'linear'
        )
        setId = newSet.id
        setCaptureSetId(setId)
      }
      
      // GraphManagerに画像を保存
      logger.log('[CapturePage] Before addImageToSet - component still mounted')
      await addImageToSet(setId, frame.dataUrl)
      logger.log('[CapturePage] After addImageToSet - checking camera state')
      logger.log('[CapturePage] cameraStarted:', cameraStarted.current, 'videoRef:', !!videoRef.current)
      
      // Check if still mounted before updating state
      if (!isMountedRef.current) {
        logger.log('[CapturePage] Component unmounted, skipping state updates')
        return
      }
      
      // UI用に画像URLを保持 - 関数形式で更新して依存を減らす
      setCapturedImages(prev => [...prev, frame.dataUrl])
      setLastCapturedImage(frame.dataUrl)
      
      logger.log('[CapturePage] Capture completed successfully')
      // カメラが閉じていないことを確認
      logger.log('[CapturePage] After capture - cameraStarted:', cameraStarted.current)
    } catch (error) {
      logger.error('[CapturePage] Capture error:', error)
      // エラーが発生してもカメラは継続
    } finally {
      isCapturingRef.current = false
      setIsCapturing(false)
    }
  }, [captureImage, captureSetId, createSet, addImageToSet, mode])
  
  // 戻るボタンハンドラ
  const handleBack = useCallback(() => {
    stopCamera()
    cameraStarted.current = false
    if (mode === null) {
      navigateTo('home')
    } else {
      setMode(null)
      setCapturedImages([])
      setLastCapturedImage(undefined)
      setCaptureSetId(null)
    }
  }, [mode, navigateTo, stopCamera])
  
  // クリーンアップ（コンポーネントのアンマウント時のみ）
  useEffect(() => {
    const cameraAdapterRef = cameraAdapter.current
    return () => {
      // コンポーネントが本当にアンマウントされる時だけ停止
      logger.log('[CapturePage] Cleanup effect, unmounting component')
      logger.log('[CapturePage] cameraStarted.current:', cameraStarted.current)
      logger.log('[CapturePage] cameraAdapter.current:', !!cameraAdapterRef)
      
      // refの現在値を直接参照
      if (cameraStarted.current && cameraAdapterRef) {
        logger.log('[CapturePage] Stopping camera on unmount')
        cameraAdapterRef.stop()
        cameraStarted.current = false
      }
    }
  }, []) // 空の依存配列で、マウント/アンマウント時のみ実行
  
  // モード未選択時
  if (mode === null) {
    logger.log('[CapturePage] Mode is null, showing ModeSelector')
    return <ModeSelector onModeSelect={handleSelectMode} onCancel={() => navigateTo('home')} />
  }
  
  // 向きガイド表示時
  if (showOrientationGuide) {
    return (
      <OrientationGuide
        onContinue={handleOrientationContinue}
        onCancel={handleBack}
        onIgnore={handleOrientationIgnore}
      />
    )
  }
  
  // カメラ画面
  return (
    <div 
      className="bg-black text-white relative h-screen w-full overflow-hidden"
      style={{ 
        height: '100dvh',
        maxHeight: '100dvh'
      }}
    >
      {/* ヘッダー */}
      <div className="absolute top-0 left-0 right-0 z-10 flex items-center justify-between p-3 sm:p-4 pt-safe-top bg-gradient-to-b from-black/80 to-transparent">
        <button
          onClick={handleBack}
          className="p-2 bg-gray-800/80 hover:bg-gray-700/80 rounded-lg transition-colors"
        >
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
        </button>
        
        <div className="text-center">
          <h2 className="text-lg font-semibold">
            {mode === 'normal' ? '通常撮影' : 'パノラマ撮影'}
          </h2>
          {mode === 'panorama' && (
            <p className="text-xs text-gray-400 mt-1">
              {orientation === 'landscape' ? '横向きモード' : '縦向きモード'}
            </p>
          )}
        </div>
        
        {/* 完了ボタン */}
        {capturedImages.length > 0 && (
          <button
            onClick={() => {
              logger.log('[CapturePage] Complete button clicked')
              logger.log('[CapturePage] captureSetId:', captureSetId)
              logger.log('[CapturePage] capturedImages:', capturedImages.length)
              
              // Use safe navigation to prevent double clicks and race conditions
              void safeNavigate(() => {
                // Clear all pending timeouts first
                timeoutRefs.current.forEach(timeout => clearTimeout(timeout))
                timeoutRefs.current.clear()
                
                // Stop camera and cleanup
                stopCamera()
                cameraStarted.current = false
                
                // Always navigate to decks after capture completion
                // This avoids the complex state transitions that cause React Error #185
                logger.log('[CapturePage] Navigating to decks after capture completion')
                navigateTo('decks')
              })
            }}
            className="px-3 py-1.5 sm:px-4 sm:py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors text-sm font-medium disabled:opacity-50"
            disabled={isNavigating()}
          >
            完了 ({capturedImages.length})
          </button>
        )}
        {capturedImages.length === 0 && <div className="w-10"></div>}
      </div>
      
      {/* カメラプレビュー */}
      <div className="absolute inset-0">
        <CameraPreview
          ref={videoRef}
          isActive={isActive}
          showGrid={showGrid}
          showGhost={showGhost && mode === 'panorama'}
          ghostImage={lastCapturedImage}
          aspectRatio={mode === 'panorama' ? '16:9' : '4:3'}
          orientation={orientation === 'unknown' ? 'portrait' : orientation}
        />
      </div>
      
      {/* コントロール */}
      <CaptureControls
        isActive={isActive}
        isLoading={isLoading || isCapturing}
        cameras={availableCameras}
        currentCamera={currentCamera}
        zoomLevel={zoomLevel}
        torchEnabled={torchEnabled}
        showGrid={showGrid}
        showGhost={showGhost}
        orientation={orientation === 'unknown' ? 'portrait' : orientation}
        capturedCount={capturedImages.length}
        onCapture={() => void handleCapture()}
        onSwitchCamera={() => void switchCamera()}
        onSetZoom={(level) => void setZoom(level)}
        onToggleTorch={() => void setTorch(!torchEnabled)}
        onToggleGrid={() => setShowGrid(!showGrid)}
        onToggleGhost={() => setShowGhost(!showGhost)}
      />
      
      {/* エラー表示 */}
      {error && !errorDismissed && startCamera && (
        <div className="absolute top-20 left-4 right-4 bg-red-600/90 text-white p-4 rounded-lg shadow-lg">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <p className="font-medium mb-1">エラーが発生しました</p>
              <p className="text-sm opacity-90">{error.message}</p>
              {error.message.includes('permission') && (
                <p className="text-xs mt-2 opacity-75">
                  カメラへのアクセスを許可してください
                </p>
              )}
            </div>
            <button
              onClick={() => setErrorDismissed(true)}
              className="ml-4 p-1 hover:bg-red-700/50 rounded transition-colors"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          {!isActive && (
            <button
              onClick={() => {
                setErrorDismissed(true)
                if (videoRef.current) {
                  void startCamera(videoRef.current)
                }
              }}
              className="mt-3 w-full py-2 bg-white/20 hover:bg-white/30 rounded text-sm font-medium transition-colors"
            >
              再試行
            </button>
          )}
        </div>
      )}
    </div>
  )
}